import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { CommonRoute } from '../enums/common-route.enum';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { CountryAvailabilityService } from '../services/country-availability/country-availability.service';
import { LogFactory } from '../services/logger/log-factory';
import { PlatformService } from '../services/platform/platform.service';

export const CountryAvailabilityGuard: CanActivateFn = (_route, state) => {
  const platform = inject(PlatformService);
  const router = inject(Router);
  const availability = inject(CountryAvailabilityService);
  const logger = inject(LogFactory).createLogger('CountryAvailabilityGuard');

  if (platform.isNative()) return true;

  const unavailableTree = () =>
    router.createUrlTree([CommonRoute.COUNTRY_UNAVAILABLE], {
      queryParams: { returnUrl: state.url },
    });

  const authentication = inject(AuthenticationService);

  // Initial navigation can beat asynchronous localStorage hydration. Waiting
  // ensures a returning user's token is attached to this request, so the API
  // evaluates their stable account country instead of misclassifying them as a
  // guest from the current VPN country.
  return authentication.whenInitialized().pipe(
    switchMap(() => availability.getAvailability()),
    map((result) => (result.available ? true : unavailableTree())),
    // Only a definitive verdict (HTTP 200 with available:false) blocks. An
    // unreachable or erroring API is an outage, not a country answer — showing
    // the release-gate screen for it misdiagnoses the failure and strands the
    // user on a page whose every action needs the same API. Failing open is
    // safe: WebCountryAvailabilityGuard enforces availability server-side on
    // every non-exempt request, so a blocked visitor still cannot register,
    // check out, or reach a gated endpoint.
    catchError((error: unknown) => {
      logger.warn(
        'Country availability check failed; allowing navigation',
        error,
      );
      return of(true);
    }),
  );
};
